import { Content } from "@/components/layouts/content";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_dashboard/changelog")({
	component: ChangelogPage,
});

interface ChangelogEntry {
	version: string;
	date: string;
	changes: {
		type: "added" | "changed" | "fixed" | "removed";
		description: string;
	}[];
}

const changelog: ChangelogEntry[] = [
	{
		version: "1.9.0",
		date: "2026-05-22",
		changes: [
			{
				type: "fixed",
				description: "InvoiceRequest → Invoice link is now created automatically when a request is marked 'Processed'. Previously the system just flipped the status flag without creating the actual Invoice row, leaving requests in a half-state where the Collection Receipt tab was useless and JEs couldn't post.",
			},
			{
				type: "added",
				description: "New artisan command `invoices:backfill-from-requests` (with `--dry-run`) to materialize Invoice rows for any historical requests stuck in the 'Processed without Invoice' state. Idempotent — safe to re-run.",
			},
			{
				type: "added",
				description: "InvoiceGenerationService::generateInvoiceFromRequest() — service method any flow can call to materialize an Invoice from an InvoiceRequest. Returns the existing Invoice if already linked (idempotent).",
			},
		],
	},
	{
		version: "1.8.2",
		date: "2026-05-22",
		changes: [
			{
				type: "fixed",
				description: "Cancel invoice button now correctly detects when the AR invoice OR collection JE has been posted in Books — even if it was posted manually via Books's admin UI (previously, the local status would stay at 'draft' forever, blocking cancellation).",
			},
			{
				type: "fixed",
				description: "On every InvoiceRequest page load, Salesmon silently pings Books to verify the actual current status of any 'draft' JE mirrors (both the AR invoice JE and the Collection JE) and updates the local copy if Books has moved on.",
			},
			{
				type: "fixed",
				description: "Tooltip on the disabled Cancel invoice button now actually shows on hover (was using native title attribute which doesn't work on disabled buttons).",
			},
		],
	},
	{
		version: "1.8.1",
		date: "2026-05-22",
		changes: [
			{
				type: "added",
				description: "Cancel invoice button on the Invoice Request detail page (where Finance actually works). Resolves the underlying Invoice and calls the existing Books cancellation flow. CANCELLED badge + cancellation reason banner show when applicable.",
			},
			{
				type: "added",
				description: "Smart button gating: hidden when the request has no invoice yet, disabled with explanatory tooltip when the Books journal entry is still a draft (must be posted before cancel can flow through to Books), and hidden when already cancelled.",
			},
		],
	},
	{
		version: "1.8.0",
		date: "2026-05-22",
		changes: [
			{
				type: "added",
				description:
					"Invoice cancellation (AR Guide Module 8, BIR-compliant). Cancel button on the invoice detail page opens a modal that requires a reason (3-1000 chars). For paid invoices, a warning explains the negative AR will be auto-reclassified to Customer Deposits in Books. The cancellation is atomic: if Books rejects, the Salesmon invoice stays uncancelled.",
			},
			{
				type: "added",
				description:
					"BIR 2307 Certificates module — track creditable withholding tax certificates from customers. Aging dashboard (5 buckets), per-row Verify and Mark Filed workflow, math drift detection, status filter, full pagination. Backend at /api/bir-2307 with 8 endpoints.",
			},
			{
				type: "changed",
				description: "Invoice resource now includes cancellation audit fields (cancelled_at, cancelled_by, cancellation_reason, books JE references).",
			},
		],
	},
	{
		version: "1.4.0",
		date: "2026-03-05",
		changes: [
			{
				type: "changed",
				description: "Lead reference number is now auto-generated when Proposal Date, Category, and Client Type are set — no more manual Generate button",
			},
		],
	},
	{
		version: "1.3.0",
		date: "2026-03-03",
		changes: [
			{ type: "added", description: "Lead detail page — view full lead info, contact details, dates & status, and activity history" },
			{ type: "added", description: "Lead notes with optional file attachments — post notes, attach files, and download attachments" },
			{ type: "added", description: "Customizable lead statuses — create, edit, reorder, and set default statuses with color coding" },
			{ type: "added", description: "Lead categories with combobox selection" },
			{ type: "added", description: "Leads Excel import with batch mode support" },
			{ type: "added", description: "Invoice requests module — create, track, and manage invoice requests with status workflow and document generation" },
		],
	},
	{
		version: "1.2.0",
		date: "2026-02-18",
		changes: [
			{ type: "added", description: "Leads module — track sales proposals/leads with category tabs, search, status filters, and full CRUD" },
			{ type: "added", description: "Leads Excel export with current filters applied" },
			{ type: "added", description: "Sales Contracts Report Excel export with multi-sheet workbook (Summary + per-salesperson sheets)" },
			{ type: "added", description: "Changelog page" },
		],
	},
	{
		version: "1.0.0",
		date: "2026-02-01",
		changes: [
			{ type: "added", description: "Sales contracts management with CRUD, extensions, renewals, and invoice generation" },
			{ type: "added", description: "Collection report with monthly tracking and export (PDF, CSV, Excel)" },
			{ type: "added", description: "Projects and clients management" },
			{ type: "added", description: "Sales dashboard with metrics and calendar view" },
			{ type: "added", description: "Sales Contracts Report with salesperson tabs and summary" },
			{ type: "added", description: "User registration with approval workflow" },
			{ type: "added", description: "Ask AI (RAG-powered document search and chat)" },
		],
	},
];

const typeBadge: Record<string, { label: string; className: string }> = {
	added: { label: "Added", className: "bg-green-100 text-green-800" },
	changed: { label: "Changed", className: "bg-blue-100 text-blue-800" },
	fixed: { label: "Fixed", className: "bg-yellow-100 text-yellow-800" },
	removed: { label: "Removed", className: "bg-red-100 text-red-800" },
};

function ChangelogPage() {
	return (
		<Content pageName="Changelog">
			<div className="flex flex-col w-full max-w-3xl p-6 gap-8">
				<div className="flex flex-col gap-1">
					<span className="text-xl text-foreground-dark font-semibold">Changelog</span>
					<span className="text-base text-foreground/70">What's new and improved in Salesmon</span>
				</div>

				<div className="flex flex-col gap-8">
					{changelog.map((entry) => (
						<div key={entry.version} className="flex flex-col gap-3">
							<div className="flex items-center gap-3">
								<span className="text-lg font-semibold text-foreground-dark">v{entry.version}</span>
								<span className="text-sm text-foreground/50">
									{new Date(entry.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
								</span>
							</div>
							<ul className="flex flex-col gap-2 pl-1">
								{entry.changes.map((change, i) => (
									<li key={`${entry.version}-${i}`} className="flex items-start gap-2">
										<span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 mt-0.5 ${typeBadge[change.type].className}`}>
											{typeBadge[change.type].label}
										</span>
										<span className="text-sm text-gray-700">{change.description}</span>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
		</Content>
	);
}
